import { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { usePublicSubscriptionStatus } from "@/hooks/usePublicSubscriptionStatus";
import { toast } from "sonner";
import { trackConversion } from "@/lib/gtag";
const Reservations = () => {
  const [date, setDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [guests, setGuests] = useState<string>("2");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Cancellation state
  const [cancelPhone, setCancelPhone] = useState("");
  const [cancelDate, setCancelDate] = useState<Date>();
  const [cancelDateOpen, setCancelDateOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Availability state
  const [maxReservations, setMaxReservations] = useState(50);
  const [currentReservations, setCurrentReservations] = useState(0);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [reservationsEnabled, setReservationsEnabled] = useState(true);

  const { hasReservationsAccess } = usePublicSubscriptionStatus();

  useEffect(() => {
    fetchSettings();
    fetchBlockedDates();
  }, []);

  useEffect(() => {
    if (date) {
      fetchDayReservations(date);
    }
  }, [date]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("reservation_settings")
        .select("*")
        .single();

      if (error && error.code !== "PGRST116") throw error;
      
      if (data) {
        setMaxReservations(data.max_reservations_per_day);
        setReservationsEnabled(data.reservations_enabled);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const { data, error } = await supabase
        .from("blocked_dates")
        .select("date");

      if (error) throw error;
      setBlockedDates((data || []).map(d => d.date));
    } catch (error) {
      console.error("Error fetching blocked dates:", error);
    }
  };

  const fetchDayReservations = async (selectedDate: Date) => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("reservations")
        .select("id", { count: "exact" })
        .eq("reservation_date", dateStr)
        .neq("status", "cancelled");

      if (error) throw error;
      setCurrentReservations(data?.length || 0);
    } catch (error) {
      console.error("Error fetching day reservations:", error);
    }
  };

  const allTimes = [
    "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
    "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
    "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM",
    "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM",
    "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM",
    "8:00 PM", "8:30 PM"
  ];

  // Filter out past times if selected date is today
  const availableTimes = allTimes.filter((time) => {
    if (!date) return true;
    
    const today = new Date();
    const selectedDate = new Date(date);
    
    // If selected date is not today, show all times
    if (selectedDate.toDateString() !== today.toDateString()) {
      return true;
    }
    
    // Parse the time string
    const [timeStr, period] = time.split(" ");
    let [hours, minutes] = timeStr.split(":").map(Number);
    
    // Convert to 24-hour format
    if (period === "PM" && hours !== 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }
    
    // Create a date object for the time slot
    const timeSlot = new Date(selectedDate);
    timeSlot.setHours(hours, minutes, 0, 0);
    
    // Only show times that are in the future
    return timeSlot > today;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reservationsEnabled) {
      toast.error("Reservations are currently disabled. Please try again later.");
      return;
    }

    if (!date || !selectedTime || !firstName || !lastName) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (currentReservations >= maxReservations) {
      toast.error("Sorry, we've reached maximum capacity for this day. Please choose another date.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert time to 24-hour format
      const [time, period] = selectedTime.split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      
      if (period === "PM" && hours !== 12) {
        hours += 12;
      } else if (period === "AM" && hours === 12) {
        hours = 0;
      }

      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

      const { error } = await supabase.from("reservations").insert({
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phone || null,
        party_size: parseInt(guests),
        reservation_date: format(date, "yyyy-MM-dd"),
        reservation_time: timeString,
        status: "booked"
      });

      if (error) throw error;

      // Track enhanced conversion
      trackConversion('reservation_submitted', {
        email: email || undefined,
        phone_number: phone || undefined,
        address: {
          first_name: firstName,
          last_name: lastName,
        }
      });

      // Send confirmation email if email is provided
      if (email) {
        try {
          await supabase.functions.invoke("send-reservation-email", {
            body: {
              type: "confirmation",
              email: email,
              firstName: firstName,
              lastName: lastName,
              reservationDate: format(date, "yyyy-MM-dd"),
              reservationTime: selectedTime,
              partySize: parseInt(guests),
              phone: phone || undefined,
            },
          });
        } catch (emailError) {
          console.error("Error sending confirmation email:", emailError);
          // Don't fail the reservation if email fails
        }
      }

      if (email) {
        toast.success("Reservation submitted successfully! A confirmation email has been sent. Please check your spam folder if you don't see it in your inbox.");
      } else {
        toast.success("Reservation submitted successfully!");
      }
      
      // Reset form
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setGuests("2");
      setDate(undefined);
      setSelectedTime("");
      setCurrentReservations(prev => prev + 1);
    } catch (error) {
      console.error("Error submitting reservation:", error);
      toast.error("Failed to submit reservation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelReservation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cancelPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }

    if (!cancelDate) {
      toast.error("Please select the reservation date");
      return;
    }

    setIsCancelling(true);

    try {
      const dateStr = format(cancelDate, "yyyy-MM-dd");
      
      // Find reservations with this phone number and date
      const { data: reservations, error: fetchError } = await supabase
        .from("reservations")
        .select("*")
        .eq("phone", cancelPhone)
        .eq("reservation_date", dateStr)
        .neq("status", "cancelled");

      if (fetchError) throw fetchError;

      if (!reservations || reservations.length === 0) {
        toast.error("No active reservation found for this phone number and date");
        return;
      }

      // Cancel the found reservations
      const { error: updateError } = await supabase
        .from("reservations")
        .update({ status: "cancelled" })
        .eq("phone", cancelPhone)
        .eq("reservation_date", dateStr)
        .neq("status", "cancelled");

      if (updateError) throw updateError;

      // Send cancellation email if reservation has email
      if (reservations.length > 0 && reservations[0].email) {
        try {
          await supabase.functions.invoke("send-reservation-email", {
            body: {
              type: "cancellation",
              email: reservations[0].email,
              firstName: reservations[0].first_name,
              lastName: reservations[0].last_name,
              reservationDate: dateStr,
              reservationTime: reservations[0].reservation_time,
              partySize: reservations[0].party_size,
              phone: reservations[0].phone || undefined,
            },
          });
        } catch (emailError) {
          console.error("Error sending cancellation email:", emailError);
          // Don't fail the cancellation if email fails
        }
      }

      toast.success(`Reservation cancelled successfully for ${format(cancelDate, "MMMM dd, yyyy")}`);
      setCancelPhone("");
      setCancelDate(undefined);
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      toast.error("Failed to cancel reservation. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  if (!hasReservationsAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Reservations Unavailable</h1>
          <p className="text-muted-foreground">Reservations are currently disabled.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-3 py-6 md:px-4 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-4xl font-bold mb-2">Junnior's Tacos</h1>
            <p className="text-xs md:text-base text-muted-foreground">4.7 (4K reviews) • Restaurant</p>
            <p className="text-xs md:text-base text-muted-foreground">727 Bankhead Hwy, Carrollton, Ga 30117</p>
          </div>

          <Tabs defaultValue="book" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="book" className="text-xs md:text-sm">Book Reservation</TabsTrigger>
              <TabsTrigger value="cancel" className="text-xs md:text-sm">Cancel Reservation</TabsTrigger>
            </TabsList>

            {/* Book Reservation Tab */}
            <TabsContent value="book">
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 bg-card p-4 md:p-8 rounded-lg shadow-lg">
                {/* Availability Info */}
                {date && (
                  <div className="flex items-center justify-between p-3 md:p-4 bg-muted rounded-lg">
                    <div>
                      <p className="text-xs md:text-sm font-medium">Reservations Available</p>
                      <p className="text-xs text-muted-foreground">
                        {format(date, "MMMM dd, yyyy")}
                      </p>
                    </div>
                    <Badge 
                      variant={currentReservations >= maxReservations ? "destructive" : "default"}
                      className="text-xs md:text-lg px-2 md:px-4 py-1 md:py-2"
                    >
                      {maxReservations - currentReservations} / {maxReservations}
                    </Badge>
                  </div>
                )}

                {!reservationsEnabled && (
                  <div className="p-3 md:p-4 bg-destructive/10 border border-destructive rounded-lg">
                    <p className="text-xs md:text-sm text-destructive font-medium">
                      Reservations are currently disabled. Please check back later.
                    </p>
                  </div>
                )}

                {/* Guest Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label htmlFor="firstName" className="text-xs md:text-sm">First Name *</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="text-sm md:text-base h-9 md:h-10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-xs md:text-sm">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="text-sm md:text-base h-9 md:h-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label htmlFor="email" className="text-xs md:text-sm">Email (Optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="text-sm md:text-base h-9 md:h-10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs md:text-sm">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      className="text-sm md:text-base h-9 md:h-10"
                    />
                  </div>
                </div>

                {/* Reservation Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <Label className="text-xs md:text-sm">Guests *</Label>
                    <Select value={guests} onValueChange={setGuests}>
                      <SelectTrigger className="text-sm md:text-base h-9 md:h-10">
                        <SelectValue placeholder="2 Guests" />
                      </SelectTrigger>
                      <SelectContent className="bg-background z-50 text-sm">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} {num === 1 ? "Guest" : "Guests"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs md:text-sm">Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal text-sm md:text-base h-9 md:h-10 px-3",
                            !date && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4 shrink-0" />
                          {date ? format(date, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(checkDate) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const dateStr = format(checkDate, "yyyy-MM-dd");
                            return checkDate < today || blockedDates.includes(dateStr);
                          }}
                          initialFocus
                          className="pointer-events-auto text-sm"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-xs md:text-sm">Time *</Label>
                    <Select value={selectedTime} onValueChange={setSelectedTime}>
                      <SelectTrigger className="text-sm md:text-base h-9 md:h-10">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent className="bg-background z-50 text-sm">
                        {availableTimes.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full text-sm md:text-base"
                  size="lg"
                  disabled={isSubmitting || !reservationsEnabled || (date && currentReservations >= maxReservations)}
                >
                  {isSubmitting ? "Submitting..." : "Book Reservation"}
                </Button>
              </form>
            </TabsContent>

            {/* Cancel Reservation Tab */}
            <TabsContent value="cancel">
              <form onSubmit={handleCancelReservation} className="space-y-4 md:space-y-6 bg-card p-4 md:p-8 rounded-lg shadow-lg">
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2">Cancel Your Reservation</h3>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Enter the phone number and date for the reservation you want to cancel.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="cancelPhone" className="text-xs md:text-sm">Phone Number *</Label>
                    <Input
                      id="cancelPhone"
                      type="tel"
                      value={cancelPhone}
                      onChange={(e) => setCancelPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      required
                      className="text-sm md:text-base h-9 md:h-10"
                    />
                  </div>

                  <div>
                    <Label className="text-xs md:text-sm">Reservation Date *</Label>
                        <Popover open={cancelDateOpen} onOpenChange={setCancelDateOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal text-sm md:text-base h-9 md:h-10",
                                !cancelDate && "text-muted-foreground"
                              )}
                            >
                              <Calendar className="mr-2 h-3 md:h-4 w-3 md:w-4" />
                              {cancelDate ? format(cancelDate, "PPP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={cancelDate}
                              onSelect={(date) => {
                                setCancelDate(date);
                                setCancelDateOpen(false);
                              }}
                              initialFocus
                              className="pointer-events-auto text-sm"
                            />
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs md:text-sm text-muted-foreground mt-2">
                          Select the date of the reservation you want to cancel.
                        </p>
                      </div>

                      <Button 
                        type="submit" 
                        variant="destructive" 
                        className="w-full text-sm md:text-base"
                        size="lg"
                        disabled={isCancelling}
                      >
                        {isCancelling ? "Cancelling..." : "Cancel Reservation"}
                      </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Reservations;
